import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../context/AuthContext';
import { getAppBridge, shouldUseAppBridge } from '../AppBridge';

export function useShopifyAuth() {
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Safely extract from context with memoized fallbacks
  const setToken = useMemo(
    () => authContext?.setToken || (() => {}),
    [authContext?.setToken]
  );
  
  const setUser = useMemo(
    () => authContext?.setUser || (() => {}),
    [authContext?.setUser]
  );
  
  const token = authContext?.token;

  const API = process.env.REACT_APP_API_URL;

  const applyAuthResult = useCallback(
    async (data, shop) => {
      // Store token and user data
      setToken(data.access_token);
      setUser({
        id: data.user_id,
        shop: data.shop || shop,
        isEmbedded: true,
        email: data.email || `${shop}@shopify-embedded.baobrain.com`,
      });

      // Persist in storage
      sessionStorage.setItem('shopifyEmbeddedToken', data.access_token);
      sessionStorage.setItem('jwtToken', data.access_token); 
      sessionStorage.setItem('shopifyShop', data.shop || shop);
      sessionStorage.setItem('isShopifyEmbedded', 'true');
      
      // Also store in localStorage for persistence
      localStorage.setItem('token', data.access_token);
    },
    [setToken, setUser]
  );

  const fallbackShopAuth = useCallback(
    async (shop, host) => {
      const resp = await fetch(`${API}/shopify/session-auth-fallback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop, host }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Fallback session auth failed');
      }
      
      const data = await resp.json();
      return data;
    },
    [API]
  );

  // Helper function to get session token from the new CDN-hosted App Bridge
  const getSessionTokenFromAppBridge = useCallback(async (app) => {
    try {
      // New CDN App Bridge uses idToken() method
      if (app && typeof app.idToken === 'function') {
        const token = await app.idToken();
        return token;
      }
      
      // Fallback for older versions
      if (app && typeof app.getSessionToken === 'function') {
        return await app.getSessionToken();
      }
      
      throw new Error('No session token method available on App Bridge instance');
    } catch (err) {
      console.error('[useShopifyAuth] Failed to get session token:', err);
      throw err;
    }
  }, []);

  useEffect(() => {
    async function authenticateWithSessionToken() {
      const urlParams = new URLSearchParams(window.location.search);
      const host = urlParams.get('host');
      const shop = urlParams.get('shop');

      // Not in embedded context - skip
      if (!host || !shop) {
        setLoading(false);
        return;
      }

      // Check if we should use App Bridge
      if (!shouldUseAppBridge()) {
        setLoading(false);
        return;
      }

      // Check if we already have a valid token
      if (token) {
        try {
          const { jwtDecode } = await import('jwt-decode');
          const decoded = jwtDecode(token);
          const isExpired = Date.now() >= (decoded.exp * 1000) - 5000;
          
          if (!isExpired) {
            setLoading(false);
            return;
          }
        } catch (e) {
          // Could not decode existing token, proceeding with auth
        }
      }

      // Get App Bridge instance (window.shopify from the new CDN)
      const app = getAppBridge();

      // If no App Bridge, use fallback immediately
      if (!app) {
        try {
          const data = await fallbackShopAuth(shop, host);
          await applyAuthResult(data, shop);
          setLoading(false);
        } catch (e) {
          console.error('[useShopifyAuth] Fallback auth failed:', e);
          setError(e.message);
          setLoading(false);
        }
        return;
      }

      // Try session token auth with App Bridge
      try {
        const sessionToken = await getSessionTokenFromAppBridge(app);
        
        if (!sessionToken) {
          throw new Error('Could not get session token from App Bridge');
        }

        const response = await fetch(`${API}/shopify/session-auth`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ shop, host }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Session auth failed');
        }

        const data = await response.json();
        
        await applyAuthResult(data, shop);
        setLoading(false);
        
      } catch (err) {
        console.warn('[useShopifyAuth] Session token auth failed, trying fallback:', err.message);
        
        // Try fallback auth
        try {
          const data = await fallbackShopAuth(shop, host);
          await applyAuthResult(data, shop);
          setLoading(false);
        } catch (e) {
          console.error('[useShopifyAuth] All auth methods failed:', e);
          setError(e.message);
          setLoading(false);
        }
      }
    }

    authenticateWithSessionToken();

    // Refresh token periodically (every 50 minutes)
    const interval = setInterval(authenticateWithSessionToken, 50 * 60 * 1000);

    // Refresh on window focus
    const handleFocus = () => {
      authenticateWithSessionToken();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [API, applyAuthResult, fallbackShopAuth, token, getSessionTokenFromAppBridge]);

  return { loading, error };
}